import { routeHref } from '../../lib/router.js'
import { mailboxName, sourceLabel, sourceProvider } from '../../lib/mail.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- mail-sidebar ---
let __fez_component_0;
{
const FOLDERS = [
  { value: 'inbox', label: 'Inbox', icon: 'tray' },
  { value: 'all', label: 'All mail', icon: 'mailbox' },
  { value: 'feed', label: 'Feed', icon: 'newspaper' },
  { value: 'papertrail', label: 'Paper Trail', icon: 'receipt' },
  { value: 'spam', label: 'Spam', icon: 'warning-octagon' },
  { value: 'starred', label: 'Starred', icon: 'star' },
  { value: 'assigned', label: 'Assigned to me', icon: 'user-circle-check' },
  { value: 'notes', label: 'Notes', icon: 'note' },
  { value: 'snoozed', label: 'Snoozed', icon: 'clock' },
  { value: 'drafts', label: 'Drafts', icon: 'file-text' },
  { value: 'sent', label: 'Sent', icon: 'paper-plane-tilt' },
  { value: 'archive', label: 'Archive', icon: 'archive' },
  { value: 'trash', label: 'Trash', icon: 'trash' },
]
__fez_component_0 = class {
  PROPS = {
    containers: Array,
    container: String,
    mailbox: String,
    folder: { type: String, default: 'inbox' },
    addr: String,
    host: String,
    unread: Boolean,
    counts: Object,
    labels: Array,
    label: String,
    sources: Array,
    source: String,
    forwards: Array,
    notify: Boolean,
    on_compose: { type: Function, state: true, default: () => {} },
    on_notify: { type: Function, state: true, default: () => {} },
    on_sources: { type: Function, state: true, default: () => {} },
  }

  beforeRender() {
    const { container, mailbox, folder, addr, host, label, source } = this.props
    const unread = this.props.unread ? '1' : ''
    const inFolder = folder === 'inbox' ? '' : folder
    const counts = this.props.counts || {}
    const badge = (value) => (value > 99 ? '99+' : value > 0 ? String(value) : '')
    const groups = this.props.containers
    // Counts add up over the mailboxes in view.
    const inView = groups.flatMap((group) => group.mailboxes.filter((box) => !mailbox || box.ref === mailbox).map((box) => box.ref))
    const filters = { container, mailbox, host, addr, source }
    const folderLink = (item) => ({
      ...item,
      href: routeHref('inbox', { ...filters, folder: item.value === 'inbox' ? '' : item.value }),
      active: item.value === folder && !label && !(unread && item.value === 'inbox'),
      count: badge(inView.reduce((sum, ref) => sum + (counts[ref]?.[item.value] || 0), 0)),
      strong: item.value === 'inbox',
    })
    this.state.folders = FOLDERS.map(folderLink)

    const current = { container, mailbox, folder: inFolder, unread, host, addr, label, source }
    const several = groups.reduce((sum, group) => sum + group.mailboxes.length, 0) > 1
    this.state.unreadHref = routeHref('inbox', { ...filters, unread: '1' })
    this.state.filtered = Boolean(unread || host || addr || label || source || (mailbox && several))
    this.state.clearHref = routeHref('inbox', { folder: inFolder })
    this.state.boxes = several
      ? groups.flatMap((group) =>
          group.mailboxes.map((box) => {
            const active = group.ref === container && box.ref === mailbox
            return {
              ref: box.ref,
              line: `${group.name} / ${box.display_name || mailboxName(box)}`,
              href: routeHref('inbox', active ? { folder: inFolder, unread } : { container: group.ref, mailbox: box.ref, folder: inFolder, unread }),
              active,
              unread: badge(counts[box.ref]?.inbox || 0),
            }
          }),
        )
      : []
    const group = groups.find((item) => item.ref === container)
    const box = mailbox && group?.mailboxes.find((item) => item.ref === mailbox)
    // A picked mailbox lists its own addresses instead of the domains.
    const domains = box ? [] : [...new Set(groups.flatMap((item) => item.domains))]
    this.state.domains =
      domains.length > 1 || (!box && host)
        ? domains.map((name) => ({ name, active: name === host, href: routeHref('inbox', { ...current, host: name === host ? '' : name, addr: '' }) }))
        : []

    // Labels, sources, addresses and aliases belong to one mailbox.
    const href = (params) => routeHref('inbox', { container, mailbox, unread, host, ...params })
    this.state.labels = box ? this.props.labels.map((item) => ({ ...item, href: href({ label: item.ref }), active: item.ref === label })) : []
    this.state.sources = box ? this.props.sources.map((item) => this.sourceLink(item, href, inFolder)) : []
    // Read-only members see the sources there are, and nothing to set up.
    this.state.sourcesShown = Boolean(box) && (this.props.sources.length > 0 || this.props.forwards.length > 0 || box.can_send !== false)
    this.state.forwarding = box ? this.forwarding() : null
    // An address is the mailbox's name on one domain: it shows that domain's mail, aliases included.
    this.state.addresses = box
      ? group.domains.map((domain) => {
          const active = domain === host && !addr
          return { value: `${mailboxName(box)}@${domain}`, href: routeHref('inbox', { ...current, host: active ? '' : domain, addr: '' }), active }
        })
      : []
    // The name and its aliases, as local parts: each one shows only the mail sent to it.
    this.state.aliases =
      box && box.aliases?.length > 1
        ? box.aliases.map((local) => ({
            value: `${local}@`,
            href: routeHref('inbox', { ...current, addr: local === addr ? '' : local }),
            active: local === addr,
          }))
        : []
  }

  // The sidebar line for where the open mailbox forwards: one address, or how many.
  forwarding() {
    const items = this.props.forwards
    if (!items.length) return null
    const waiting = items.some((item) => item.status !== 'active' || item.suppressed)
    return {
      label: items.length === 1 ? `To ${items[0].address}` : `To ${items.length} addresses`,
      flag: waiting ? 'warning' : '',
    }
  }

  sourceLink(item, href, inFolder) {
    const active = item.ref === this.props.source
    const waiting = Boolean(item.confirm)
    const detected = item.status === 'detected'
    return {
      ref: item.ref,
      label: sourceLabel(item),
      icon: sourceProvider(item.address).icon,
      color: item.color,
      unread: item.unread > 99 ? '99+' : item.unread > 0 ? String(item.unread) : '',
      href: href({ folder: inFolder, source: active ? '' : item.ref }),
      active,
      flag: waiting ? 'warning' : detected ? 'accent' : '',
    }
  }

  CSS = `:fez {
display:block;nav{display:flex;flex-direction:column;gap:var(--space-1);padding:var(--space-4) var(--space-3) var(--space-6)}.side-compose{margin-bottom:var(--space-3)}.side-group{display:flex;align-items:baseline;justify-content:space-between;gap:var(--space-2);padding:var(--space-4) var(--space-3) var(--space-1);color:var(--color-text-subtle);font-size:var(--text-xs);font-weight:var(--weight-bold);letter-spacing:0.06em;text-transform:uppercase}.side-clear{color:var(--color-text-muted);font-weight:var(--weight-semibold);letter-spacing:normal;text-transform:none;&:hover{color:var(--color-text)}}.side-aliases{display:flex;flex-wrap:wrap;gap:var(--space-1) var(--space-2);padding:var(--space-1) var(--space-3);font-size:var(--text-xs)}.side-alias{color:var(--color-text-subtle);text-decoration:none;&:hover{color:var(--color-text);text-decoration:none}&.active{color:var(--color-selected-text);font-weight:var(--weight-semibold)}}.side-link-label{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.side-folders{display:flex;flex-direction:column;gap:1px}.side-link{display:flex;align-items:center;gap:var(--space-3);height:34px;padding:0 var(--space-3);border-radius:var(--radius-full);color:var(--color-text-muted);font-size:var(--text-sm);text-decoration:none;transition:background var(--duration-fast) var(--ease),color var(--duration-fast) var(--ease);&:hover{background:var(--color-bg-hover);color:var(--color-text);text-decoration:none}&.active{background:var(--color-selected-bg);color:var(--color-selected-text);font-weight:var(--weight-semibold)}}.side-link-label{flex:1;min-width:0}.side-count{flex-shrink:0;min-width:20px;color:var(--color-text-subtle);font-size:var(--text-xs);font-weight:var(--weight-semibold);font-variant-numeric:tabular-nums;text-align:right;&.strong{color:var(--color-text);font-weight:var(--weight-bold)}}.side-foot{margin-top:var(--space-6);padding-top:var(--space-3);border-top:1px solid var(--color-border)}.side-label-dot{width:10px;height:10px;margin:0 4px;flex:none;border-radius:var(--radius-full)}.side-sub{padding:var(--space-3) var(--space-3) var(--space-1);color:var(--color-text-subtle);font-size:var(--text-xs);font-weight:var(--weight-semibold)}.side-sub-tools{display:flex;align-items:center;justify-content:space-between;padding-right:var(--space-2)}.side-sub-button{display:inline-grid;place-items:center;width:24px;height:24px;padding:0;border:0;border-radius:var(--radius-full);background:transparent;color:var(--color-text-subtle);cursor:pointer;&:hover{background:var(--color-bg-hover);color:var(--color-text)}}.side-source-icon{display:inline-grid;place-items:center;width:18px;flex:none}.side-flag{width:8px;height:8px;flex:none;border-radius:var(--radius-full);background:var(--color-accent);&.warning{background:var(--color-warning)}}
}`

  HTML = `<nav aria-label="Mail">
<div class="side-compose">
<ui-button variant="primary" size="lg" block icon="pencil-simple" label="Compose" title="Compose (c)" onclick="fez.state.on_compose()"></ui-button>
</div>
<div class="side-folders">
{#each state.folders as item}
<a class="side-link" class:active={item.active} href={item.href} key={item.value}>
{#if item.active}<ui-icon name={item.icon} size="18" weight="fill"></ui-icon>{:else}<ui-icon name={item.icon} size="18"></ui-icon>{/if}
<span class="side-link-label">{item.label}</span>
{#if item.count}<span class="side-count" class:strong={item.strong}>{item.count}</span>{/if}
</a>
{#if item.value === 'inbox'}
<a class="side-link" class:active={props.unread} href={state.unreadHref}>
{#if props.unread}<ui-icon name="envelope-simple" size="18" weight="fill"></ui-icon>{:else}<ui-icon name="envelope-simple" size="18"></ui-icon>{/if}
<span class="side-link-label">Unread only</span>
</a>
{/if}
{/each}
</div>
<div class="side-group">
<span>Filters</span>
{#if state.filtered}<a class="side-clear" href={state.clearHref}>Clear</a>{/if}
</div>
<div class="side-folders">
{#if state.boxes.length}
<div class="side-sub">Mailboxes</div>
{#each state.boxes as box}
<a class="side-link" class:active={box.active} href={box.href} key={box.ref} aria-current={box.active ? 'true' : 'false'}>
<ui-icon name="tray" size="18"></ui-icon>
<span class="side-link-label">{box.line}</span>
{#if box.unread}<span class="side-count">{box.unread}</span>{/if}
</a>
{/each}
{/if}
{#if state.addresses.length}
<div class="side-sub">Addresses</div>
{#each state.addresses as item}
<a class="side-link" class:active={item.active} href={item.href} key={item.value}>
<ui-icon name="at" size="18"></ui-icon>
<span class="side-link-label">{item.value}</span>
</a>
{/each}
{/if}
{#if state.domains.length}
<div class="side-sub">Domains</div>
{#each state.domains as item}
<a class="side-link" class:active={item.active} href={item.href} key={item.name}>
<ui-icon name="globe" size="18"></ui-icon>
<span class="side-link-label">{item.name}</span>
</a>
{/each}
{/if}
{#if state.labels.length}
<div class="side-sub">Labels</div>
{#each state.labels as item}
<a class="side-link" class:active={item.active} href={item.href} key={item.ref}>
<span class="side-label-dot" style="background: var(--color-avatar-{item.color})"></span>
<span class="side-link-label">{item.name}</span>
{#if item.threads}<span class="side-count">{item.threads}</span>{/if}
</a>
{/each}
{/if}
{#if state.sourcesShown}
<div class="side-sub side-sub-tools">
<span class="side-sub-title">Sources</span>
<button type="button" class="side-sub-button" title="Sources and forwarding" aria-label="Sources and forwarding" onclick="fez.state.on_sources()"><ui-icon name="gear-six" size="14"></ui-icon></button>
</div>
{#each state.sources as item}
<a class="side-link" class:active={item.active} href={item.href} key={item.ref}>
<span class="side-source-icon" style="color: var(--color-avatar-{item.color})"><ui-icon name={item.icon} size="16"></ui-icon></span>
<span class="side-link-label">{item.label}</span>
{#if item.flag}<span class="side-flag {item.flag}"></span>{/if}
{#if item.unread}<span class="side-count">{item.unread}</span>{/if}
</a>
{/each}
{#if state.forwarding}
<button type="button" class="side-link side-toggle" onclick="fez.state.on_sources('forwarding')">
<span class="side-source-icon"><ui-icon name="arrow-bend-up-right" size="16"></ui-icon></span>
<span class="side-link-label">{state.forwarding.label}</span>
{#if state.forwarding.flag}<span class="side-flag {state.forwarding.flag}"></span>{/if}
</button>
{/if}
{#if !state.sources.length && !state.forwarding}
<button type="button" class="side-link side-toggle" onclick="fez.state.on_sources()">
<ui-icon name="plus" size="18"></ui-icon>
<span class="side-link-label">Add a source</span>
</button>
{/if}
{/if}
{#if state.aliases.length}
<div class="side-sub">Aliases</div>
<div class="side-aliases">
{#each state.aliases as item}
<a class="side-alias" class:active={item.active} href={item.href} key={item.value}>{item.value}</a>
{/each}
</div>
{/if}
</div>
<div class="side-foot">
<button type="button" class="side-link side-toggle" onclick="fez.state.on_notify()">
{#if props.notify}<ui-icon name="bell-ringing" size="18"></ui-icon>{:else}<ui-icon name="bell-slash" size="18"></ui-icon>{/if}
<span class="side-link-label">{props.notify ? 'Notifications on' : 'Notifications off'}</span>
</button>
</div>
</nav>`
};
}

Fez('mail-sidebar', __fez_component_0);

export default __fez_component_0;
